import { Box } from "@chakra-ui/react"
import * as React from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { SDKContext } from "../../../context/SDKContext"
import { MessageInput } from "../message-input"
import {
  chatAttachmentDataAtom,
  chatAttachmentReplyDataAtom,
  chatIsReadSelector,
  currentChatGenerationAtom,
  isSavingChatAttachment,
  isSavingChatAttachmentReply
} from "../../../recoil/atoms/chats"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
import layoutStates from "../../../recoil/atoms/layout"
import { useTranslation } from "react-i18next"
import useSecretChatMessage from "../../../hooks/useSecretChatMessage"
import ChatAttachmentPreview from "./_chatAttachmentPreview"
import { avatarCommunication } from "src/conditionals/Avatar/AvatarCommunication"
import useConfig from "src/hooks/useConfig"
import { useHandleAttachmentUpload } from "src/hooks/useHandleAttachmentUpload"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"

interface ChatInputProps {}

const _ChatInput: React.FC<ChatInputProps> = () => {
  const { t } = useTranslation()
  const { showAttachmentInMvwOnSend } = useConfig()
  const meetingClient = React.useContext(SDKContext)
  const setChatIsRead = useSetRecoilState(chatIsReadSelector)
  const [positionModal, setPositionModal] = React.useState({ x: 0, y: 0 })
  const room = useRecoilValue(roomSelector)
  const handleAssignPosition = event => {
    setPositionModal({ x: 110, y: event.clientY - 30 })
  }
  const roomStyles = useRecoilValue(brandingSelector)

  const [attachment, setAttachment] = useRecoilState(chatAttachmentDataAtom)
  const [replyAttachment] = useRecoilState(chatAttachmentReplyDataAtom)
  const { triggerWatchPartyOnUpload } = useHandleAttachmentUpload()
  const setIsSavingAttachment = useSetRecoilState(isSavingChatAttachment)
  const setIsSavingChatAttachmentReply = useSetRecoilState(
    isSavingChatAttachmentReply
  )
  const sidebarOpen = useRecoilValue<boolean>(layoutStates.openSidebar)
  const isSidePanelWithSingleTab = useRecoilValue<boolean>(
    layoutStates.isSidePanelWithSingleTab
  )
  const { handleSecretChatMessage } = useSecretChatMessage()
  const avatarConfig = useCurrentAvatarInfo()
  const generation = useRecoilValue(currentChatGenerationAtom)

  const handleSendChat = React.useCallback(
    (processedText, parentMessageId) => {
      if (!processedText.length) return
      // -> Handle the secret message and send non secret message to server

      const isSecretMessage = handleSecretChatMessage(processedText)

      if (!isSecretMessage) {
        console.log("generation", generation)
        meetingClient?.sendPublicChat(
          processedText,
          parentMessageId,
          null,
          undefined,
          avatarConfig?.id?.toString(),
          generation
        )
        setChatIsRead(true)
      }

      // communicate with the trulience iframe
      avatarCommunication.sendMessage(processedText)
    },
    [meetingClient, setChatIsRead, handleSecretChatMessage]
  )

  const handleSendChatAttachment = (chatMessage, parentChatId) => {
    const attachmentData = parentChatId ? replyAttachment.data : attachment.data

    meetingClient?.sendPublicChatAttachment({
      chatMessage,
      attachment: attachmentData,
      userId: "",
      parentChatId,
      toAvatarId: avatarConfig?.id?.toString(),
      generation
    })
    if (parentChatId) {
      setIsSavingChatAttachmentReply(true)
    } else {
      setIsSavingAttachment(true)
    }

    // share the attachment on MVW and display avatar in popup
    if (showAttachmentInMvwOnSend) {
      triggerWatchPartyOnUpload()
    }
  }

  const borderColor = React.useMemo<string>(() => {
    return roomStyles?.servicePanelElementBorder ?? "inherit"
  }, [
    roomStyles?.servicePanelElementBorder,
    roomStyles?.servicePanelElementWarningText
  ])

  const removeAttachment = () => setAttachment({ data: "", mimeType: "" })
  const saving = useRecoilValue(isSavingChatAttachment)

  return (
    <Box
      d="flex"
      pos="relative"
      flexDir="column"
      justifyContent="space-between"
      alignItems="flex-end"
      maxW="100%"
      pt="0"
      pb="4"
      px="4"
      marginTop={attachment.data ? null : "1rem"}
      pr={isSidePanelWithSingleTab ? "9" : undefined}
      borderTop={attachment.data ? "2px solid" : "none"}
      borderColor={roomStyles.servicePanelElementBorder}
    >
      {attachment.data && (
        <ChatAttachmentPreview
          attachment={attachment}
          removeAttachment={removeAttachment}
          roomStyles={roomStyles}
          borderColor={borderColor}
          saving={saving}
        />
      )}
      <MessageInput
        positionModal={positionModal}
        callbackEmoji={handleAssignPosition}
        inputId="expanding-input"
        offFocusFirstRender={!sidebarOpen}
        parentMessageId=""
        userId=""
        sendMessage={
          attachment.data && attachment.mimeType
            ? handleSendChatAttachment
            : handleSendChat
        }
        placeholder={t("Send a message…")}
        inputStyle={{
          // margin: "auto 0.5rem",
          paddingInlineStart:
            room?.showInteractivityRequiredVisual === true
              ? "calc(0.5rem + 3px)"
              : "0.5rem"
        }}
        inputLeftElementStyle={{
          left: "-2px",
          top: "inherit",
          color: "gray.500",
          paddingTop: "3px",
          cursor: "pointer"
        }}
        inputRightElementStyle={{
          top: "inherit",
          paddingTop: "0.2rem",
          alignItems: "center",
          width: "42px",
          display: "flex",
          justifyContent: "space-between",
          marginRight: "0.5rem"
        }}
      />
    </Box>
  )
}

export const ChatInput = React.memo(_ChatInput)
